import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingDown, Users, Repeat, Coins, Sparkles } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/apiConfig';

/**
 * CommunityImpactCard — Phase C/P5 finalization
 * Aggregated stats card for drivers showing:
 *   • km saved through corridor pickups
 *   • km saved through TSP optimization
 *   • € saved by passengers (cumulative service-fee delta)
 *   • round-trip count (commuter status)
 *
 * Calls GET /api/impact/driver. Auth required; the backend uses g.user_id.
 */
export const CommunityImpactCard = ({ token }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    axios
      .get(`${API}/impact/driver`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return (
      <div data-testid="community-impact-loading" style={loadingStyle}>
        <div className="spinner-small" />
      </div>
    );
  }
  if (!data || data.bookings_count === 0) {
    return (
      <div data-testid="community-impact-empty" style={emptyStyle}>
        <Sparkles size={18} style={{ color: '#a78bfa', marginBottom: 6 }} />
        <div style={{ fontSize: 13, color: '#cbd5e1', textAlign: 'center', maxWidth: 220 }}>
          {t('impact.empty', 'Sobald deine erste Buchung läuft, siehst du hier deinen Community-Impact.')}
        </div>
      </div>
    );
  }

  const stats = [
    {
      icon: <TrendingDown size={18} />, color: '#22c55e',
      label: t('impact.kmCorridor', 'KM gespart (Korridor)'),
      value: `${(data.km_saved_via_corridor || 0).toFixed(1)} km`,
      testid: 'impact-km-corridor',
    },
    {
      icon: <Sparkles size={18} />, color: '#3b82f6',
      label: t('impact.kmTsp', 'KM gespart (Multi-Stop)'),
      value: `${(data.km_saved_via_tsp || 0).toFixed(1)} km`,
      testid: 'impact-km-tsp',
    },
    {
      icon: <Coins size={18} />, color: '#f59e0b',
      label: t('impact.feeSaved', 'Mitfahrer-Ersparnis'),
      value: `${(data.service_fee_saved_eur || 0).toFixed(2)} €`,
      testid: 'impact-fee-saved',
    },
    {
      icon: <Repeat size={18} />, color: '#a78bfa',
      label: t('impact.roundTrips', 'Hin & Rück'),
      value: `${data.round_trip_count || 0}×`,
      testid: 'impact-round-trips',
    },
  ];

  return (
    <div
      data-testid="community-impact-card"
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: '20px 22px',
        color: '#f1f5f9',
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Users size={18} style={{ color: '#22c55e' }} />
        <strong style={{ fontSize: 14, letterSpacing: 0.3 }}>
          {t('impact.title', 'Dein Community-Impact')}
        </strong>
        <span style={{
          marginLeft: 'auto',
          fontSize: 11, color: '#94a3b8',
          padding: '2px 8px',
          background: 'rgba(255,255,255,0.06)',
          borderRadius: 999,
        }}>
          {data.bookings_count || 0} {t('impact.bookings', 'Fahrten')}
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 10,
      }}>
        {stats.map((s, i) => (
          <div
            key={i}
            data-testid={s.testid}
            style={{
              padding: '10px 12px',
              background: 'rgba(255,255,255,0.04)',
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <div style={{ color: s.color, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9' }}>{s.value}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {data.savings_pct_avg > 0 && (
        <div style={{
          marginTop: 14,
          padding: '8px 12px',
          background: 'rgba(34,197,94,0.10)',
          border: '1px solid rgba(34,197,94,0.35)',
          borderRadius: 8,
          color: '#22c55e',
          fontSize: 12, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <TrendingDown size={13} />
          {t('impact.savingsAvg', 'Ø {{pct}}% Service-Ersparnis für deine Mitfahrer', {
            pct: data.savings_pct_avg,
          })}
        </div>
      )}
    </div>
  );
};

const loadingStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 30, background: '#0f172a', borderRadius: 16,
};
const emptyStyle = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  padding: '24px 18px', background: '#1e293b', borderRadius: 16,
  border: '1px dashed rgba(167,139,250,0.3)',
};

export default CommunityImpactCard;
